import React, { useEffect, useRef } from 'react'
import styles from './ProjectTaskMentions.module.css'

/**
 * ProjectTaskMentions - Autocomplete dropdown for #project and #task mentions
 *
 * Shows a dropdown with project and task suggestions when user types '#'
 * Supports keyboard navigation (Up/Down arrows, Enter, Esc)
 */
const ProjectTaskMentions = ({
    suggestions = [],
    selectedIndex = 0,
    onSelect,
    position = { top: 0, left: 0 },
    mentionType = 'project' // 'project', 'task', 'user'
}) => {
    const listRef = useRef(null)
    const selectedRef = useRef(null)

    // Scroll to selected item
    useEffect(() => {
        if (selectedRef.current && listRef.current) {
            const itemTop = selectedRef.current.offsetTop
            const itemBottom = itemTop + selectedRef.current.offsetHeight
            const listTop = listRef.current.scrollTop
            const listBottom = listTop + listRef.current.clientHeight

            if (itemTop < listTop) {
                listRef.current.scrollTop = itemTop
            } else if (itemBottom > listBottom) {
                listRef.current.scrollTop = itemBottom - listRef.current.clientHeight
            }
        }
    }, [selectedIndex])

    if (!suggestions || suggestions.length === 0) {
        return null
    }

    return (
        <div
            className={styles.dropdown}
            style={{
                top: `${position.top}px`,
                left: `${position.left}px`
            }}
        >
            <div ref={listRef} className={styles.list}>
                {suggestions.map((item, index) => {
                    const isSelected = index === selectedIndex

                    const displayName = item.display_name || item.full_name || item.name || item.email
                    const username = item.username

                    return (
                        <div
                            key={item.id}
                            ref={isSelected ? selectedRef : null}
                            className={`${styles.item} ${isSelected ? styles.selected : ''}`}
                            onClick={() => onSelect(item)}
                        >
                            <div className={styles.itemContent}>
                                {mentionType === 'user' ? (
                                    <div className={styles.userRow}>
                                        <div className={styles.avatarWrapper}>
                                            {item.avatar_url ? (
                                                <img src={item.avatar_url} alt={displayName} className={styles.avatarImage} />
                                            ) : (
                                                <div className={styles.avatarFallback}>
                                                    {displayName?.charAt(0)?.toUpperCase() || '?'}
                                                </div>
                                            )}
                                        </div>
                                        <div className={styles.userMeta}>
                                            <span className={styles.userName}>{displayName}</span>
                                            {username && (
                                                <span className={styles.userHandle}>@{username}</span>
                                            )}
                                            {item.email && (
                                                <span className={styles.userEmail}>{item.email}</span>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className={styles.name}>
                                            {mentionType === 'task' && item.project_name && (
                                                <span className={styles.projectPrefix}>
                                                    {item.project_name} &gt;
                                                </span>
                                            )}
                                            {item.name}
                                        </div>
                                        {item.client_name && mentionType === 'project' && (
                                            <div className={styles.meta}>
                                                {item.client_name}
                                                {item.is_billable && (
                                                    <span className={styles.billable}> • ₹</span>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    )
                })}
            </div>
            <div className={styles.footer}>
                <span className={styles.hint}>
                    ↑↓ to navigate • Enter to select • Esc to dismiss
                </span>
            </div>
        </div>
    )
}

export default ProjectTaskMentions
